import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextType {
    /** The user's chosen mode: 'system', 'light', or 'dark' */
    mode: ThemeMode;
    /** The resolved theme actually applied: 'light' or 'dark' */
    resolvedTheme: 'light' | 'dark';
    /** Set the mode */
    setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    mode: 'system',
    resolvedTheme: 'light',
    setMode: () => {},
});

const STORAGE_KEY = 'pepite-theme-mode';

function getSystemPreference(): 'light' | 'dark' {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
    if (mode === 'system') return getSystemPreference();
    return mode;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
        } catch {}
        return 'system';
    });

    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(mode));

    // Apply the class to <html> and synchronize Android StatusBar on native mobile
    const applyTheme = useCallback((resolved: 'light' | 'dark') => {
        const root = document.documentElement;
        if (resolved === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        setResolvedTheme(resolved);

        if (Capacitor.isNativePlatform()) {
            StatusBar.setStyle({
                style: resolved === 'dark' ? Style.Dark : Style.Light,
            }).catch(() => {});
            StatusBar.setBackgroundColor({
                color: resolved === 'dark' ? '#0d0e12' : '#fafaf9',
            }).catch(() => {});
        }
    }, []);

    // When mode changes, persist and apply
    const setMode = useCallback((newMode: ThemeMode) => {
        setModeState(newMode);
        try {
            localStorage.setItem(STORAGE_KEY, newMode);
        } catch {}
        applyTheme(resolveTheme(newMode));
    }, [applyTheme]);

    // On mount, apply current
    useEffect(() => {
        applyTheme(resolveTheme(mode));
    }, []);

    // Listen for OS preference changes (only relevant when mode === 'system')
    useEffect(() => {
        if (mode !== 'system') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            applyTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [mode, applyTheme]);

    return (
        <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
